import { SearchableDropdown } from 'components/SearchableDropdown';
import { DEFAULT_PAGE_SIZE } from '@state/dealers/constants';
import { dealersLoadMore, editDealerSearchQuery, editSelectedDealer, getDealerAPI } from '@state/dealers/actions';
import { Dealer } from '@state/dealers/types';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import selectors from '@state/selectors';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { IUserType } from '@state/auth/types';


export interface RootProps {
    dealers: Dealer[]
}

const DealerSelect = (props: any) => {
    const dispatch = useDispatch();
    const authContext = React.useContext(AuthContext);
    const isAdmin = authContext.userData && authContext.userData.profile && authContext.userData.profile.role === IUserType.ADMIN;
    const userData = authContext && authContext.userData?authContext.userData.profile:undefined;
    const dealers = useSelector(selectors.dealers.getDealers);    
    const isLoadingMoreDealers = dealers.loading; 
    const isLoadingInitialDealers = dealers.page === 0  && isLoadingMoreDealers
    const canLoadMore = dealers.lastPageSize ? dealers.lastPageSize === DEFAULT_PAGE_SIZE : true        
    
    const [selectedOption, setSelectedOption] = React.useState<any>();
    
    const organizations = (): string | null => {
        if (!isAdmin) {
            const organizations = userData["dealeron.dealerorganization"];
            if (typeof organizations === 'string') {
                return JSON.stringify([organizations]);
            }

            if (Array.isArray(organizations)) {
                return JSON.stringify(organizations);
            }

            return null;
        }
        
        return JSON.stringify([]);
    };

    React.useEffect(() => {                
        dispatch(getDealerAPI(dealers.page, dealers.limit, dealers.query, organizations(), getDealerPerUser()))
    }, [dealers.page, dealers.query])

    React.useEffect(() => {
        if (dealers.selectedDealer && dealers.selectedDealer?.id) {
            setSelectedOption({id:dealers.selectedDealer.id, displayName:dealers.selectedDealer.dealerName})            
        }
    }, [dealers.selectedDealer?.id])

    const getDealerPerUser= () => {
        if(isAdmin){
            return undefined;
        }
        if (userData?.role === 'DealerUser'){
            return userData?.["dealeron.primarydealer"] ?? undefined;
        }
    };

    const filterByIdOrName = (dealer:any) => {
        //dealer is either an array of Dealers or a single Dealer object
        if(dealer===undefined) return false;
        let dlr = dealer as Dealer;
        if (Array.isArray(dealer)) {
            dlr = dealer[0] ?? undefined; 
        };
        let name = dlr.dealerName ? dlr.dealerName.toLocaleLowerCase():undefined;
        let id = dlr.id ? dlr.id.toLocaleLowerCase():undefined;
        if(!name || !id) return false;
        //return name.includes('') || id.includes(''); //This one doesn't make any sense 
        return true;
    }
    const suggestItems = React.useMemo(() => {
        if (dealers && dealers.dealers) {
            const onlyInterestedDealers = dealers.dealers.filter(filterByIdOrName);
            //dealer is either an array or a single Dealer object
            return onlyInterestedDealers.map((dealer:Dealer) => (
                {
                id: Array.isArray(dealer) ? dealer[0].id : dealer.id,
                displayName: Array.isArray(dealer) ? `${dealer[0].dealerName} - ${dealer[0].id}` : `${dealer.dealerName} - ${dealer.id}`
            }))
        }
        return undefined;

    }, [dealers])


    if (!dealers) {
        return null
    }

    return (
        <SearchableDropdown
            value={dealers.query}
            options={suggestItems}
            isLoading={Boolean(isLoadingInitialDealers)}       
            canLoadMoreOptions={canLoadMore}
            emptyText={getEmptyText()}
            placeholder={"Search a Dealer"}
            readonly={false}
            onValueChange={(v) => onSearchQueryChange(v)}
            onOptionSelected={onSelectedItem}
            onDrawerDismissed={onDrawerDismissed}
            onLoadMoreOptions={loadMoreOptions}
            selectedOption={selectedOption}
        />
    )

    function onSearchQueryChange(query:any) {
        dispatch(editDealerSearchQuery(query))
    }

    function loadMoreOptions() {        
        dispatch(dealersLoadMore())
    }

    function getEmptyText() {
        if (isLoadingInitialDealers) {
            if (dealers.query === "") {
                return "Loading..."
            } else {
                return `Searching "${dealers.query}"`
            }
        } else {
            if (dealers.query !== "") {
                return `No Dealers found for "${dealers.query}"`
            } else {
                return `No Dealers found`
            }
        }    
    }

    function onDrawerDismissed() {
        dispatch(editDealerSearchQuery(""));
    }

    function onSelectedItem(item:any) {
        setSelectedOption(item)
        const dealer = dealers.dealers?.find((d:Dealer) => d.id === item.id)
        if (dealer) {
            dispatch(editSelectedDealer(dealer))
        }
    }
}

export default DealerSelect;
